import { useEffect, useRef } from 'react';

export const getPageType = (path: string) => {
  if (!path) return 'other';
  if (path === '/' || path === '/au' || path === '/nz' || path === '/au/' || path === '/nz/') return 'Home';
  if (path.includes('/product/') || path.includes('/products/')) return 'PDP';
  if (
    path.includes('/categories') ||
    path.includes('/category') ||
    path.includes('/collection') ||
    path.includes('/bathroom') ||
    path.includes('/kitchen') ||
    path.includes('/laundry') ||
    path.includes('/smart-command') ||
    path.includes('/spare-parts')
  ) {
    return 'PLP';
  }
  if (path.includes('/search')) return 'SLP';
  if (path.includes('product')) return 'PDP';
  if (path.includes('category') || path.includes('collection')) return 'PLP';
  return 'other';
};

export const trackEvent = (eventName: string, payload: any = {}) => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...payload });
  }
};

export const trackVideoProgress = (e: React.SyntheticEvent<HTMLVideoElement, Event>, trackedMilestones: React.MutableRefObject<Set<number>>) => {
  const video = e.currentTarget;
  if (!video.duration) return;
  const percentage = (video.currentTime / video.duration) * 100;
  const milestones = [25, 50, 75];
  milestones.forEach((milestone) => {
    if (percentage >= milestone && !trackedMilestones.current.has(milestone)) {
      trackedMilestones.current.add(milestone);
      trackEvent('video_progress', { progress: `${milestone}%`, src: video.currentSrc });
    }
  });
};

export const useScrollTracking = (collection_name?: string) => {
  const trackedDepths = useRef(new Set<number>());

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      if (documentHeight === windowHeight) return;

      const scrollPercentage = (scrollPosition / (documentHeight - windowHeight)) * 100;

      const milestones = [25, 50, 75, 100];

      milestones.forEach((milestone) => {
        if (scrollPercentage >= milestone && !trackedDepths.current.has(milestone)) {
          trackedDepths.current.add(milestone);
          const payload: any = { scroll_depth: `${milestone}%` };
          if (collection_name) payload.collection_name = collection_name;
          trackEvent('scroll_depth', payload);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [collection_name]);
};

export const useTimeOnPageTracking = (collection_name?: string) => {
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const startTime = Date.now();
    const handleLeave = () => {
      if (hasTrackedRef.current) return;
      hasTrackedRef.current = true;

      const time_spent = Math.max(1, Math.round((Date.now() - startTime) / 1000));
      const payload: any = { time_spent };
      if (collection_name) payload.collection_name = collection_name;
      trackEvent('time_on_page', payload);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handleLeave();
      }
    };

    window.addEventListener('beforeunload', handleLeave);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleLeave);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      handleLeave();
    };
  }, [collection_name]);
};
